import { Injectable, Optional } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { environment } from 'projects/aumico-project-reports/src/environments/environment';
import { BehaviorSubject, distinctUntilChanged, filter, Observable, shareReplay, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { PrintJobCompletedPayload } from '../interfaces/printJobCompletedPayLoad.interface';
import { NotificationSocketPayloadDto } from '../interfaces/user-notification-socket-payload.interface';

export type SocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/** Disconnect reason socket.io reports when the server closed the connection. */
const SERVER_DISCONNECT_REASON = 'io server disconnect';
/** Wait time before the first reconnect attempt; doubles with each attempt. */
const RECONNECT_BASE_DELAY_MS = 1000;
/** Upper bound for the wait time between reconnect attempts. */
const RECONNECT_MAX_DELAY_MS = 30_000;
/** Number of consecutive reconnect attempts before giving up. */
const MAX_RECONNECT_ATTEMPTS = 5;
/** How long a connection must hold before it counts as healthy. */
const HEALTHY_CONNECTION_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class NotificationsSocketService {
    private socket: Socket | null = null;

    /** Token from the last connect() call, used when no Firebase user is available. */
    private fallbackToken = '';
    /** Consecutive reconnect attempts since the last successful connect. */
    private reconnectAttempts = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    /** When the current connection was established. */
    private connectedSince: number | null = null;

    private readonly statusSubject = new BehaviorSubject<SocketStatus>('disconnected');
    public readonly status$: Observable<SocketStatus> = this.statusSubject.asObservable().pipe(distinctUntilChanged());

    private readonly notificationSubject = new BehaviorSubject<NotificationSocketPayloadDto | null>(null);
    public readonly notification$: Observable<NotificationSocketPayloadDto> = this.notificationSubject
        .asObservable()
        .pipe(filter((n): n is NotificationSocketPayloadDto => n != null), shareReplay(1));

    private readonly printJobCompletedSubject = new Subject<PrintJobCompletedPayload>();
    public readonly printJobCompleted$: Observable<PrintJobCompletedPayload> = this.printJobCompletedSubject.asObservable();

    // Optional: the component lab pulls this service in at bootstrap without Firebase Auth.
    public constructor(@Optional() private readonly auth: Auth | null) {}

    /**
     * Connect to the notifications websocket.
     * @param token Raw JWT idToken (without "Bearer " prefix)
     */
    public connect(token: string): void {
        if (this.socket) {
            this.disconnect();
        }

        const url = this.resolveSocketUrl();

        this.fallbackToken = token;
        this.reconnectAttempts = 0;
        this.connectedSince = null;
        this.statusSubject.next('connecting');

        this.socket = io(url, {
            transports: ['websocket', 'polling'],
            // socket.io calls this on every connection attempt, including reconnects.
            auth: (cb: (data: object) => void) => {
                void this.resolveToken()
                    .then((freshToken) => cb({ token: freshToken }))
                    .catch(() => cb({ token: this.fallbackToken }));
            },
            path: '/socket.io',
        });

        this.socket.on('connect', () => {
            // [MYC-DEBUG] fires on first connect AND every reconnect — a connect right after a disconnect = the gap that drops the event.
            console.log(`[MYC-DEBUG] WS connect id=${this.socket?.id} @ ${new Date().toISOString()}`);
            this.connectedSince = Date.now();
            this.statusSubject.next('connected');
        });

        this.socket.on('disconnect', (reason) => {
            // [MYC-DEBUG] a disconnect during an in-flight export loses the result event (no replay).
            console.warn(`[MYC-DEBUG] WS disconnect reason=${reason} @ ${new Date().toISOString()}`);
            this.statusSubject.next('disconnected');

            // A connection that held long enough clears the attempt count.
            const heldFor = this.connectedSince == null ? 0 : Date.now() - this.connectedSince;
            if (heldFor >= HEALTHY_CONNECTION_MS) {
                this.reconnectAttempts = 0;
            }
            this.connectedSince = null;

            if (reason === SERVER_DISCONNECT_REASON) {
                this.scheduleReconnect();
            }
        });

        this.socket.on('connect_error', (err: any) => {
            console.error('[NotificationsSocket] connect_error', {
                message: err?.message,
                description: err?.description,
                type: err?.type,
            });
            this.statusSubject.next('error');
        });

        this.socket.on('notification.created', (payload: NotificationSocketPayloadDto) => {
            this.notificationSubject.next(payload);
        });

        this.socket.on('print-job.completed', (payload: PrintJobCompletedPayload) => {
            // [MYC-DEBUG] proves the event actually reached THIS browser (if absent on a failure => delivery loss upstream).
            console.log(
                `[MYC-DEBUG] WS recv print-job.completed jobId=${(payload as { jobId?: string }).jobId ?? 'n/a'} success=${(payload as { success?: boolean }).success} dataLen=${(payload as { data?: string }).data?.length ?? 0} @ ${new Date().toISOString()}`,
            );
            this.printJobCompletedSubject.next(payload);
        });
    }

    public disconnect(): void {
        this.clearReconnectTimer();
        this.reconnectAttempts = 0;
        this.fallbackToken = '';

        if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
            this.statusSubject.next('disconnected');
        }
    }

/** Returns a Firebase ID token (refreshes if needed), or the one passed to connect(). */
    private async resolveToken(): Promise<string> {
        const user = this.auth?.currentUser;
        if (!user) {
            return this.fallbackToken;
        }

        try {
            return await user.getIdToken();
        } catch {
            return this.fallbackToken;
        }
    }

    /** Reconnects after a server-side disconnect, with a growing delay and a fixed attempt limit. */
    private scheduleReconnect(): void {
        if (this.reconnectTimer != null) {
            return;
        }

        if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            // [MYC-DEBUG] the socket stays closed from here on; every export will time out until connect() runs again.
            console.warn(
                `[MYC-DEBUG] WS reconnect gave up after ${this.reconnectAttempts} attempts @ ${new Date().toISOString()}`,
            );
            this.statusSubject.next('error');
            return;
        }

        const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** this.reconnectAttempts, RECONNECT_MAX_DELAY_MS);
        this.reconnectAttempts += 1;

        // [MYC-DEBUG] pairs with the WS connect line above — shows whether the re-arm actually recovered the socket.
        console.warn(
            `[MYC-DEBUG] WS reconnect attempt=${this.reconnectAttempts} in=${delay}ms @ ${new Date().toISOString()}`,
        );

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.statusSubject.next('connecting');
            this.socket?.connect();
        }, delay);
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer != null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    /**
     * Derive socket URL from apiGateway.
     * apiGateway = "http://localhost:3800/api/" → socket URL = "http://localhost:3800/notifications"
     */
    private resolveSocketUrl(): string {
        const apiGateway = environment?.modeso?.apiGateway ?? '';
        // Strip trailing /api/ or /api to get the base host
        const base = apiGateway.replace(/\/api\/?$/, '');
        return `${base}/notifications`;
    }
}
